import { create } from 'zustand';

import {
	type ChatMessage,
	mapMessageToChatMessage,
	type TaskFile,
} from '@/entities/interview-task';
import {
	type AIAttachmentV1,
	AIDialogStatusV1,
	AIProblemTaskTypeV1,
	type AIVisibleMessageV1,
	type GetAIDialogResponseV1,
} from '@/shared/api';

import { completeSelectTaskAction } from '../actions/complete-select-task-action';
import { completeStaticTaskAction } from '../actions/complete-static-task-action';
import { getDialogAction } from '../actions/get-dialog-action';
import { listMessagesAction } from '../actions/list-messages-action';
import {
	type ReportDialogResult,
	reportDialogAction,
} from '../actions/report-dialog-action';
import {
	type SendMessageResult,
	sendMessageAction,
} from '../actions/send-message-action';
import {
	type StopDialogResult,
	stopDialogAction,
} from '../actions/stop-dialog-action';
import { submitCodeAction } from '../actions/submit-code-action';

type State = {
	dialogId: string | null;
	dialog: GetAIDialogResponseV1 | null;
	messages: ChatMessage[];
	/** Пришедшие, но ещё не показанные сообщения — раскрываются по очереди. */
	pending: ChatMessage[];
	isThinking: boolean;
	error: string | null;
	lastMessageId: number;
};

type Actions = {
	initialize: (args: {
		dialogId: string;
		initialDialog: GetAIDialogResponseV1;
		initialMessages: AIVisibleMessageV1[];
	}) => void;
	reset: () => void;
	refreshDialog: () => Promise<void>;
	loadMessages: () => Promise<void>;
	/** Показывает следующее сообщение из очереди `pending` (внутреннее). */
	_revealNext: () => void;
	sendMessage: (
		text: string,
		attachFiles?: TaskFile[],
	) => Promise<SendMessageResult>;
	completeStaticTask: (taskId: string) => Promise<void>;
	completeSelectTask: (taskId: string, optionId: string) => Promise<void>;
	submitCode: (problemId: string, files: AIAttachmentV1[]) => Promise<void>;
	stopDialog: () => Promise<StopDialogResult>;
	/** Завершение интервью по таймеру → запрос генерации отчёта. */
	reportDialog: () => Promise<ReportDialogResult>;
};

type AiDialogStore = State & Actions;

const initialState: State = {
	dialogId: null,
	dialog: null,
	messages: [],
	pending: [],
	isThinking: false,
	error: null,
	lastMessageId: 0,
};

// Пауза между показом подряд идущих сообщений ИИ — чтобы они появлялись
// по очереди, а не все разом.
const REVEAL_DELAY_MS = 700;

// Таймер раскрытия очереди `pending`. Живёт на уровне модуля (store — синглтон),
// поэтому его достаточно одного.
let revealTimer: ReturnType<typeof setTimeout> | null = null;
const clearRevealTimer = () => {
	if (revealTimer) {
		clearTimeout(revealTimer);
		revealTimer = null;
	}
};

const formatNow = () => {
	const d = new Date();
	return `${String(d.getHours()).padStart(2, '0')}:${String(
		d.getMinutes(),
	).padStart(2, '0')}`;
};

/**
 * Сообщение можно отправлять только когда бэк готов его принять —
 * то есть диалог не занят генерацией и не завершён.
 */
export const canSendInStatus = (
	status: AIDialogStatusV1 | undefined,
): boolean =>
	status === AIDialogStatusV1.Idle || status === AIDialogStatusV1.AiError;

/**
 * Добавляет одно server-сообщение к уже отображённым:
 * - Если messageId уже известен — ничего не делаем (дедуп).
 * - Optimistic-«моё» сообщение (clientId, без messageId) подменяется на
 *   соответствующее server-сообщение того же текста — иначе после поллинга
 *   видели бы два своих сообщения подряд.
 */
const mergeOne = (prev: ChatMessage[], f: ChatMessage): ChatMessage[] => {
	if (
		typeof f.messageId === 'number' &&
		prev.some((m) => m.messageId === f.messageId)
	) {
		return prev;
	}
	if (f.role === 'you' && typeof f.messageId === 'number') {
		const optimisticIdx = prev.findIndex(
			(m) =>
				m.role === 'you' &&
				m.messageId == null &&
				m.clientId != null &&
				(m.text ?? '') === (f.text ?? ''),
		);
		if (optimisticIdx >= 0) {
			// Сохраняем clientId оптимистичной записи — он используется как
			// стабильный React-key, чтобы при подтверждении сообщения React
			// не размонтировал и не «переанимировал» обёртку. Прикреплённые
			// файлы (отправленный код) тоже сохраняем, если бэк не вернул их в
			// эхо-сообщении — иначе карточки файлов «мигнут» и исчезнут.
			const next = prev.slice();
			next[optimisticIdx] = {
				...f,
				clientId: prev[optimisticIdx].clientId,
				attaches: f.attaches ?? prev[optimisticIdx].attaches,
			};
			return next;
		}
	}
	return [...prev, f];
};

/**
 * При переходе диалога в code-таску уже загруженные сообщения интервьюера
 * могли быть смаппены с прежним (не-code) currentTask → файл кандидата стал
 * read-only (view). Сообщения не перемаппливаются при смене currentTask, поэтому
 * здесь конвертируем такие view-файлы в редактируемые edit (поля идентичны).
 */
const remapMessagesForCodeTask = (
	messages: ChatMessage[],
	currentTask: GetAIDialogResponseV1['currentTask'],
): ChatMessage[] => {
	if (currentTask?.taskType !== AIProblemTaskTypeV1.Code) return messages;
	let changed = false;
	const next = messages.map((m) => {
		if (m.role !== 'interviewer' || !m.attaches?.length) return m;
		let attachChanged = false;
		const attaches = m.attaches.map((f) => {
			if (f.kind !== 'view') return f;
			attachChanged = true;
			return { ...f, kind: 'edit' as const };
		});
		if (!attachChanged) return m;
		changed = true;
		return { ...m, attaches };
	});
	return changed ? next : messages;
};

export const useAiDialogStore = create<AiDialogStore>()((set, get) => ({
	...initialState,

	initialize: ({ dialogId, initialDialog, initialMessages }) => {
		clearRevealTimer();
		const mapped = initialMessages.map((m) =>
			mapMessageToChatMessage(m, initialDialog.currentTask),
		);
		const maxId = initialMessages.reduce(
			(max, m) => (m.id > max ? m.id : max),
			0,
		);
		set({
			dialogId,
			dialog: initialDialog,
			messages: mapped,
			pending: [],
			lastMessageId: maxId,
			isThinking: false,
			error: null,
		});
	},

	reset: () => {
		clearRevealTimer();
		set(initialState);
	},

	refreshDialog: async () => {
		const dialogId = get().dialogId;
		if (!dialogId) return;
		try {
			const data = await getDialogAction(dialogId);
			if (data)
				set((state) => ({
					dialog: data,
					messages: remapMessagesForCodeTask(state.messages, data.currentTask),
				}));
		} catch (e) {
			set({ error: (e as Error).message });
		}
	},

	loadMessages: async () => {
		const { dialogId, lastMessageId, dialog } = get();
		if (!dialogId) return;
		try {
			const incoming = await listMessagesAction(
				dialogId,
				lastMessageId || undefined,
			);
			if (!incoming.length) return;
			const mapped = incoming.map((m) =>
				mapMessageToChatMessage(m, dialog?.currentTask),
			);
			// Дедуп по messageId против уже показанных и ещё ожидающих сообщений.
			const knownIds = new Set(
				[...get().messages, ...get().pending]
					.map((m) => m.messageId)
					.filter((id): id is number => typeof id === 'number'),
			);
			const fresh = mapped.filter(
				(m) => m.messageId == null || !knownIds.has(m.messageId),
			);
			if (!fresh.length) return;
			const maxId = fresh.reduce(
				(acc, m) =>
					typeof m.messageId === 'number' && m.messageId > acc
						? m.messageId
						: acc,
				0,
			);
			set((state) => ({
				pending: [...state.pending, ...fresh],
				lastMessageId: Math.max(state.lastMessageId, maxId),
			}));
			// Запускаем раскрытие очереди, если оно ещё не идёт. Первое сообщение
			// показывается сразу, последующие — с паузой (см. _revealNext).
			if (!revealTimer) get()._revealNext();
		} catch (e) {
			set({ error: (e as Error).message });
		}
	},

	_revealNext: () => {
		revealTimer = null;
		const { pending } = get();
		if (!pending.length) return;
		const [head, ...rest] = pending;
		set((state) => ({
			messages: mergeOne(state.messages, head),
			pending: rest,
		}));
		// Пока в очереди остаются сообщения — показываем «AI печатает» (через
		// pending в useAiDialog) и ждём паузу перед следующим.
		if (rest.length) {
			revealTimer = setTimeout(() => get()._revealNext(), REVEAL_DELAY_MS);
		}
	},

	sendMessage: async (text, attachFiles) => {
		const { dialogId, dialog } = get();
		if (!dialogId) return { ok: false, error: null };
		if (!canSendInStatus(dialog?.status)) return { ok: false, error: null };
		const optimistic: ChatMessage = {
			role: 'you',
			time: formatNow(),
			text,
			clientId: `${Date.now()}-${Math.random()}`,
			attaches: attachFiles?.length ? attachFiles : undefined,
		};
		set((state) => ({
			messages: [...state.messages, optimistic],
			isThinking: true,
		}));
		// Откат оптимистичной записи, если бэк не принял сообщение.
		const rollback = () =>
			set((state) => ({
				messages: state.messages.filter(
					(m) => m.clientId !== optimistic.clientId,
				),
			}));
		// Отправляем содержимое отредактированных файлов как вложения сообщения
		// (для code-таски). fileName/content — единственное, что нужно бэку.
		const attachments = attachFiles?.length
			? attachFiles.map((f) => ({
					fileName: f.name,
					content: f.kind === 'edit' || f.kind === 'view' ? f.code : '',
				}))
			: undefined;
		try {
			const result = await sendMessageAction(dialogId, text, attachments);
			if (!result.ok) {
				rollback();
				return result;
			}
			await get().refreshDialog();
			await get().loadMessages();
			return result;
		} catch {
			rollback();
			return { ok: false, error: null };
		} finally {
			set({ isThinking: false });
		}
	},

	completeStaticTask: async (taskId) => {
		const { dialogId } = get();
		if (!dialogId) return;
		set({ isThinking: true });
		try {
			await completeStaticTaskAction(dialogId, taskId);
			await get().refreshDialog();
			await get().loadMessages();
		} finally {
			set({ isThinking: false });
		}
	},

	completeSelectTask: async (taskId, optionId) => {
		const { dialogId } = get();
		if (!dialogId) return;
		set({ isThinking: true });
		try {
			await completeSelectTaskAction(dialogId, taskId, optionId);
			await get().refreshDialog();
			await get().loadMessages();
		} finally {
			set({ isThinking: false });
		}
	},

	submitCode: async (problemId, files) => {
		const { dialogId } = get();
		if (!dialogId) return;
		set({ isThinking: true });
		try {
			await submitCodeAction(dialogId, problemId, files);
			await get().refreshDialog();
			await get().loadMessages();
		} finally {
			set({ isThinking: false });
		}
	},

	stopDialog: async () => {
		const { dialogId } = get();
		if (!dialogId) return { ok: false, error: null };
		const result = await stopDialogAction(dialogId);
		// Только при успехе подтягиваем свежий статус (→ aborted) для баннера.
		if (result.ok) await get().refreshDialog();
		return result;
	},

	reportDialog: async () => {
		const { dialogId } = get();
		if (!dialogId) return { ok: false, error: null };
		const result = await reportDialogAction(dialogId);
		// При успехе подтягиваем свежий статус (→ aiReport) для баннера.
		if (result.ok) await get().refreshDialog();
		return result;
	},
}));
